import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Comparison tables — head-to-head feature/benefit grids. Rendered from
// structured `metadata.columns` + `metadata.rows`, with `body` (markdown)
// shown beneath as the explanatory write-up.
//
// API contract:
//   GET /affiliate/content/comparison-tables →
//     { items: [{ id, title, body, programs:[],
//                 metadata: { columns: ["Feature","Voya9","Typical"],
//                             rows: [["What…","✓","✗"], …] } }] }
export default function AssetsComparison() {
  const [tables, setTables] = useState(null);
  const [program, setProgram] = useProgramFromQuery();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/comparison-tables", { auth: true });
        const items = res?.items || res?.tables || [];
        if (!cancelled) setTables(items);
      } catch {
        if (!cancelled) setTables([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!tables) return [];
    const set = new Set();
    tables.forEach((t) => (t.programs || []).forEach((p) => set.add(p)));
    return Array.from(set);
  }, [tables]);

  const visible = useMemo(() => {
    if (!tables) return [];
    return tables.filter((t) => program === "all" || (t.programs || []).includes(program));
  }, [tables, program]);

  if (tables === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="COMPARISON" italic="tables." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Head-to-head grids — what VOYA9 does vs. the typical alternative. Useful for "vs." style posts and FAQ answers.
      </p>
      {programs.length > 1 && (
        <div style={{ marginTop: 24 }}>
          <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="comparison-program" />
        </div>
      )}
      <div data-testid="comparison-list" style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 32 }}>
        {visible.length === 0 ? (
          <div data-testid="comparison-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No comparison tables match these filters.
          </div>
        ) : (
          visible.map((t) => <ComparisonCard key={t.id || t._id} table={t} />)
        )}
      </div>
    </div>
  );
}

function ComparisonCard({ table }) {
  const columns = table.metadata?.columns || [];
  const rows = table.metadata?.rows || [];
  return (
    <article
      data-testid={`comparison-${table.id || table._id}`}
      style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 22 }}
    >
      <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
        {table.title}
      </h3>
      {columns.length > 0 && rows.length > 0 && (
        <div style={{ marginTop: 18, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
            <thead>
              <tr>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      background: i === 1 ? C.peach : C.bone,
                      color: C.ink,
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      borderBottom: `2px solid ${C.ink}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      style={{
                        padding: "12px 14px",
                        borderBottom: `1px solid ${C.warmGray3}`,
                        background: j === 1 ? "rgba(244,201,178,0.08)" : "transparent",
                        verticalAlign: "top",
                        fontSize: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {table.body && (
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px dashed ${C.warmGray3}` }}>
          <MarkdownReader source={table.body} testId={`comparison-body-${table.id || table._id}`} />
        </div>
      )}
    </article>
  );
}
